import React, { useState, useEffect, useCallback } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

import { supabase } from "../../../lib/supabase";

const FREQUENCY_OPTIONS = [
  { value: "INMEDIATO", label: "Inmediato" },
  { value: "DIARIO", label: "Diario" },
  { value: "SEMANAL", label: "Semanal" },
];

const DAY_OPTIONS = [
  { value: "LUNES", label: "Lunes" },
  { value: "MARTES", label: "Martes" },
  { value: "MIERCOLES", label: "Miércoles" },
  { value: "JUEVES", label: "Jueves" },
  { value: "VIERNES", label: "Viernes" },
  { value: "SABADO", label: "Sábado" },
  { value: "DOMINGO", label: "Domingo" },
];

const TOGGLE_DEFINITIONS = [
  {
    key: "notify_user_registered",
    label: "Nuevo usuario registrado",
    description: "El administrador recibe un correo cuando alguien se registra en la plataforma.",
    editable: true,
  },
  {
    key: "notify_user_activated",
    label: "Cuenta activada",
    description: "El usuario recibe un correo al ser activado por un administrador.",
    editable: true,
  },
  {
    key: "notify_user_rejected",
    label: "Cuenta rechazada",
    description: "El usuario recibe un correo cuando su solicitud de registro es rechazada.",
    editable: true,
  },
  {
    key: "notify_welcome_first_login",
    label: "Bienvenida en primer inicio de sesión",
    description: "El usuario recibe un correo de bienvenida la primera vez que inicia sesión exitosamente.",
    editable: true,
  },
  {
    key: "notify_update_assigned",
    label: "Nueva actualización asignada",
    description: "El usuario recibe un correo cuando se le asigna una nueva actualización.",
    editable: true,
  },
  {
    key: "notify_password_reset",
    label: "Restablecimiento de contraseña",
    description: "Siempre activo. El usuario recibe un correo con el enlace de restablecimiento.",
    editable: false,
  },
  {
    key: "notify_url_expiring",
    label: "URL próxima a vencer",
    description: "Notificar al usuario 24 horas antes del vencimiento del enlace de descarga.",
    editable: true,
  },
  {
    key: "notify_download_limit_reached",
    label: "Límite de descargas alcanzado",
    description: "Notificar cuando el usuario alcanza el límite de descargas de una asignación.",
    editable: true,
  },
];

const DEFAULT_SETTINGS = {
  email_digest_frequency: "INMEDIATO",
  email_digest_time: "08:00",
  email_digest_day: "LUNES",
  signed_url_ttl_hours: "24",
  signed_url_download_limit: "0",
};

const DEFAULT_TOGGLES = {
  notify_user_registered: true,
  notify_user_activated: true,
  notify_user_rejected: true,
  notify_welcome_first_login: true,
  notify_update_assigned: true,
  notify_password_reset: true,
  notify_url_expiring: true,
  notify_download_limit_reached: true,
};

const NotificationsEmailConfig = ({ onToast }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [toggles, setToggles] = useState(DEFAULT_TOGGLES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('system_settings')
        ?.select('key, value, value_json')
        ?.in('key', [
          'email_digest_frequency',
          'email_digest_time',
          'email_digest_day',
          'signed_url_ttl_hours',
          'signed_url_download_limit',
          'notification_toggles',
        ]);
      if (error) throw error;

      const newSettings = { ...DEFAULT_SETTINGS };
      let newToggles = { ...DEFAULT_TOGGLES };

      (data || [])?.forEach((row) => {
        if (row?.key === 'notification_toggles') {
          newToggles = { ...DEFAULT_TOGGLES, ...(row?.value_json || {}) };
        } else {
          newSettings[row?.key] = row?.value || DEFAULT_SETTINGS?.[row?.key];
        }
      });

      setSettings(newSettings);
      setToggles(newToggles);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleToggleChange = (key) => {
    if (key === 'notify_password_reset') return;
    setToggles((prev) => ({ ...prev, [key]: !prev?.[key] }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const upserts = [
        { key: 'email_digest_frequency', value: settings?.email_digest_frequency, value_json: null },
        { key: 'email_digest_time', value: settings?.email_digest_time, value_json: null },
        { key: 'email_digest_day', value: settings?.email_digest_day, value_json: null },
        { key: 'signed_url_ttl_hours', value: settings?.signed_url_ttl_hours, value_json: null },
        { key: 'signed_url_download_limit', value: settings?.signed_url_download_limit, value_json: null },
        { key: 'notification_toggles', value: null, value_json: toggles },
      ];

      for (const row of upserts) {
        const { error } = await supabase
          ?.from('system_settings')
          ?.upsert(row, { onConflict: 'key' });
        if (error) throw error;
      }

      setHasChanges(false);
      onToast?.("Configuración guardada", "success");
    } catch (err) {
      onToast?.("Error al guardar la configuración", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 0.8s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Email Frequency Controls */}
      <div className="rounded-xl border p-4 md:p-6" style={{ backgroundColor: "var(--color-card)", borderColor: "var(--color-border)" }}>
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: "rgba(15,118,110,0.1)" }}>
            <Icon name="Clock" size={18} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="font-heading font-700 text-base" style={{ color: "var(--color-foreground)" }}>Frecuencia de Correos</h3>
            <p className="text-xs font-caption" style={{ color: "var(--color-muted-foreground)" }}>Controla cuándo y cómo se envían los correos de notificación</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Digest frequency */}
          <div>
            <label className="block text-sm font-caption font-500 mb-1.5" style={{ color: "var(--color-foreground)" }}>Digest de notificaciones</label>
            <select
              value={settings?.email_digest_frequency}
              onChange={(e) => handleSettingChange('email_digest_frequency', e?.target?.value)}
              className="w-full rounded-lg border px-3 py-2.5 text-sm font-caption focus:outline-none focus:ring-2"
              style={{ borderColor: "var(--color-border)", backgroundColor: "var(--color-background)", color: "var(--color-foreground)" }}
            >
              {FREQUENCY_OPTIONS?.map((o) => <option key={o?.value} value={o?.value}>{o?.label}</option>)}
            </select>
          </div>

          {/* Digest time */}
          <div>
            <label className="block text-sm font-caption font-500 mb-1.5" style={{ color: "var(--color-foreground)" }}>Hora de envío de digest</label>
            <input
              type="time"
              value={settings?.email_digest_time}
              onChange={(e) => handleSettingChange('email_digest_time', e?.target?.value)}
              className="w-full rounded-lg border px-3 py-2.5 text-sm font-caption focus:outline-none focus:ring-2"
              style={{ borderColor: "var(--color-border)", backgroundColor: "var(--color-background)", color: "var(--color-foreground)" }}
              disabled={settings?.email_digest_frequency === 'INMEDIATO'}
            />
          </div>

          {/* Digest day */}
          <div>
            <label className="block text-sm font-caption font-500 mb-1.5" style={{ color: "var(--color-foreground)" }}>Día de envío semanal</label>
            <select
              value={settings?.email_digest_day}
              onChange={(e) => handleSettingChange('email_digest_day', e?.target?.value)}
              className="w-full rounded-lg border px-3 py-2.5 text-sm font-caption focus:outline-none focus:ring-2"
              style={{ borderColor: "var(--color-border)", backgroundColor: "var(--color-background)", color: "var(--color-foreground)" }}
              disabled={settings?.email_digest_frequency !== 'SEMANAL'}
            >
              {DAY_OPTIONS?.map((o) => <option key={o?.value} value={o?.value}>{o?.label}</option>)}
            </select>
          </div>

          {/* TTL hours */}
          <div>
            <label className="block text-sm font-caption font-500 mb-1.5" style={{ color: "var(--color-foreground)" }}>TTL de URLs firmadas (horas)</label>
            <input
              type="number"
              min="1"
              max="8760"
              value={settings?.signed_url_ttl_hours}
              onChange={(e) => handleSettingChange('signed_url_ttl_hours', e?.target?.value)}
              className="w-full rounded-lg border px-3 py-2.5 text-sm font-caption focus:outline-none focus:ring-2"
              style={{ borderColor: "var(--color-border)", backgroundColor: "var(--color-background)", color: "var(--color-foreground)" }}
              placeholder="24"
            />
            <p className="text-xs font-caption mt-1" style={{ color: "var(--color-muted-foreground)" }}>Valor predeterminado: 24 horas</p>
          </div>

          {/* Download limit */}
          <div>
            <label className="block text-sm font-caption font-500 mb-1.5" style={{ color: "var(--color-foreground)" }}>Límite máximo de descargas por URL</label>
            <input
              type="number"
              min="0"
              value={settings?.signed_url_download_limit}
              onChange={(e) => handleSettingChange('signed_url_download_limit', e?.target?.value)}
              className="w-full rounded-lg border px-3 py-2.5 text-sm font-caption focus:outline-none focus:ring-2"
              style={{ borderColor: "var(--color-border)", backgroundColor: "var(--color-background)", color: "var(--color-foreground)" }}
              placeholder="0"
            />
            <p className="text-xs font-caption mt-1" style={{ color: "var(--color-muted-foreground)" }}>0 = ilimitado</p>
          </div>
        </div>
      </div>

      {/* Notification Toggles */}
      <div className="rounded-xl border p-4 md:p-6" style={{ backgroundColor: "var(--color-card)", borderColor: "var(--color-border)" }}>
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: "rgba(8,145,178,0.1)" }}>
            <Icon name="Bell" size={18} color="var(--color-secondary)" />
          </div>
          <div>
            <h3 className="font-heading font-700 text-base" style={{ color: "var(--color-foreground)" }}>Activación de Notificaciones</h3>
            <p className="text-xs font-caption" style={{ color: "var(--color-muted-foreground)" }}>Activa o desactiva el envío de correos por tipo de evento</p>
          </div>
        </div>

        <div className="space-y-3">
          {TOGGLE_DEFINITIONS?.map((def) => (
            <div
              key={def?.key}
              className="flex items-start justify-between gap-4 p-4 rounded-lg border transition-base"
              style={{
                borderColor: "var(--color-border)",
                backgroundColor: def?.editable ? "var(--color-background)" : "var(--color-muted)",
                opacity: def?.editable ? 1 : 0.75,
              }}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="font-caption font-600 text-sm" style={{ color: "var(--color-foreground)" }}>{def?.label}</p>
                  {!def?.editable && (
                    <span className="text-xs px-1.5 py-0.5 rounded font-caption" style={{ backgroundColor: "rgba(15,118,110,0.12)", color: "var(--color-primary)" }}>Siempre activo</span>
                  )}
                </div>
                <p className="font-caption text-xs" style={{ color: "var(--color-muted-foreground)" }}>{def?.description}</p>
              </div>
              <button
                onClick={() => handleToggleChange(def?.key)}
                disabled={!def?.editable}
                className="flex-shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none"
                style={{
                  backgroundColor: toggles?.[def?.key] ? "var(--color-primary)" : "var(--color-border)",
                  cursor: def?.editable ? 'pointer' : 'not-allowed',
                }}
                aria-checked={toggles?.[def?.key]}
                role="switch"
              >
                <span
                  className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform"
                  style={{ transform: toggles?.[def?.key] ? 'translateX(24px)' : 'translateX(4px)' }}
                />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      {hasChanges && (
        <div className="flex justify-end">
          <Button
            variant="default"
            iconName="Save"
            iconPosition="left"
            onClick={handleSave}
            loading={saving}
          >
            Guardar configuración
          </Button>
        </div>
      )}
    </div>
  );
};

export default NotificationsEmailConfig;
